import { ENTERPRISE_PARTNER_ID } from '@brighte/enterprise-common/constants';
import { BoxProps } from '@spark-web/box';
import { useTheme } from '@spark-web/theme';
import { CSSProperties, useMemo } from 'react';

import { usePartnerConfig } from '~/context/PartnerConfigContext';

type PartnerTheme = {
  background: string;
  card: Partial<BoxProps>;
  tooltip: Partial<BoxProps> & { tone: string; closeIconPresent?: boolean };
};

export const usePartnerTheming = (): PartnerTheme => {
  const config = usePartnerConfig();
  const theme = useTheme();

  const backgroundColour = config?.configuration?.backgroundColour ?? '#F1F4FF';
  const cardBorderRadius = (config?.configuration?.cardBorderRadius ?? 'large') as BoxProps['borderRadius'];

  const baseTooltipStyle = useMemo(
    () => ({
      width: 'auto',
      maxWidth: '350px',
      top: 30,
    }),
    [],
  );

  const createTooltipArrow = (color: string) => ({
    content: "''",
    position: 'absolute' as CSSProperties['position'],
    top: -10,
    transform: 'translateX(-45%)',
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: `10px solid ${color}`,
  });

  const styles: PartnerTheme = useMemo(() => {
    const getDefaultTooltipStyles = (backgroundColor: string, arrowColor: string) => ({
      ...baseTooltipStyle,
      backgroundColor,
      '&::before': createTooltipArrow(arrowColor),
    });

    if (config?.id === ENTERPRISE_PARTNER_ID.commbank) {
      return {
        background: backgroundColour,
        card: {
          borderRadius: cardBorderRadius,
          borderLeft: 'fieldAccent',
          css: {
            boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.20)',
          },
        },
        tooltip: {
          tone: 'neutralInverted',
          closeIconPresent: true,
          css: getDefaultTooltipStyles('#000000', '#000000'),
        },
      };
    }

    return {
      background: backgroundColour,
      card: {
        borderRadius: cardBorderRadius,
      },
      tooltip: {
        tone: 'neutral',
        borderRadius: 'small',
        border: 'fieldHover',
        closeIconPresent: false,
        css: getDefaultTooltipStyles(theme.color.background.neutral, theme.border.color.fieldHover),
      },
    };
  }, [config, backgroundColour, cardBorderRadius, theme, baseTooltipStyle]);

  return styles;
};
